import { devDebug } from '@/helpers/errorHelper'

export interface PermissionType {
  type: string
  id: string
  name: string
  system: string
  relationContactId: string
  privileges: Array<string>
}

export type PermissionListType = PermissionType[]

const isPermissionType = (item: any, index: number): item is PermissionType => {
  // Check for the existence of required properties
  if (!(item && 'type' in item && 'id' in item && 'name' in item && 'system' in item && 'privileges' in item)) {
    devDebug(
      `isPermissionType: FAILED item ${index} - Missing required property (type, id, name, system, or privileges).`,
      { item },
    )
    return false
  }

  // Check types of required properties
  if (typeof item.type !== 'string') {
    devDebug(`isPermissionType: FAILED item ${index} - 'type' is not a string. Value:`, item.type)
    return false
  }
  if (typeof item.id !== 'string') {
    devDebug(`isPermissionType: FAILED item ${index} - 'id' is not a string. Value:`, item.id)
    return false
  }
  if (typeof item.name !== 'string') {
    devDebug(`isPermissionType: FAILED item ${index} - 'name' is not a string. Value:`, item.name)
    return false
  }
  if (typeof item.system !== 'string') {
    devDebug(`isPermissionType: FAILED item ${index} - 'system' is not a string. Value:`, item.system)
    return false
  }

  // --- Logic for Optional/Nullable relationContactId ---
  // A clean check for Optional<string | null>: Must be one of: undefined, null, or string.
  const isRelationContactIdValid =
    item.relationContactId === undefined ||
    item.relationContactId === null ||
    typeof item.relationContactId === 'string'

  if (!isRelationContactIdValid) {
    devDebug(
      `isPermissionType: FAILED item ${index} - 'relationContactId' is invalid. Value:`,
      item.relationContactId,
      typeof item.relationContactId,
    )
    return false
  }

  // Check privileges array and its contents
  if (!Array.isArray(item.privileges)) {
    devDebug(`isPermissionType: FAILED item ${index} - 'privileges' is not an array. Value:`, item.privileges)
    return false
  }

  const allPrivilegesValid = item.privileges.every((privilege: any) => typeof privilege === 'string')
  if (!allPrivilegesValid) {
    const failedPrivilege = item.privileges.find((priv: any) => typeof priv !== 'string')
    devDebug(
      `isPermissionType: FAILED item ${index} - 'privileges' array contains non-string item. Example failure:`,
      failedPrivilege,
    )
    return false
  }

  devDebug(`isPermissionType: SUCCESS item ${index} - Passed all checks.`)
  return true
}

export const isPermissionsListType = (response: any): response is PermissionListType => {
  if (!Array.isArray(response)) {
    devDebug('isPermissionsListType: FINAL FAIL - Response is not an array.', { response })
    return false
  }

  if (response.length === 0) {
    devDebug('isPermissionsListType: FINAL SUCCESS - Response is an empty array.')
    return true
  }

  const allItemsValid = response.every((item: any, index: number) => isPermissionType(item, index))

  if (!allItemsValid) {
    devDebug('isPermissionsListType: FINAL FAIL - At least one item in the array was invalid.')
  } else {
    devDebug('isPermissionsListType: FINAL SUCCESS - All items in the array are valid.')
  }

  return allItemsValid
}
